import { forwardRef } from 'react';
import type { CouponProps } from '@elfsight/widget-ui';
import { Coupon } from '@elfsight/widget-ui';
import { useBanner } from '../../bannerProvider';
import { Block, type BlockProps } from '../_block';
import { type CouponMode, useCouponCode } from './useCouponCode';

export type CouponBlockProps = BlockProps &
  CouponProps & {
    mode?: CouponMode;
    couponCode?: string;
    couponList?: string;
    prefix?: string;
    length?: number;
    characters?: string;
    fullWidth: boolean;
    widgetId: string;
    id: string;
    editMode?: boolean;
    mobileFont: CouponProps['font'];
    applyMobileSettings: boolean;
  };

export const CouponBlock = forwardRef<HTMLDivElement, CouponBlockProps>(
  (
    {
      alignment,
      style,
      mode,
      couponCode,
      couponList,
      prefix,
      length,
      characters,
      fullWidth,
      widgetId,
      id,
      editMode,
      font,
      mobileFont,
      applyMobileSettings,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { contentDirection, trackGoogleAnalyticsEvents } = useBanner();
    const isFullWidth = contentDirection !== 'row' && fullWidth;
    const currentFont = applyMobileSettings ? mobileFont : font;

    const code = useCouponCode({
      mode,
      couponCode,
      couponList,
      prefix,
      length,
      characters,
      widgetId,
      blockId: id,
      editMode
    });

    const handleCopy = () => {
      if (!trackGoogleAnalyticsEvents || !code) {
        return;
      }

      const valueMapping = {
        coupon: code
      };

      trackGoogleAnalyticsEvents({
        eventType: 'couponCopied',
        valueMapping
      });
    };

    // Don't render if no code
    if (!code) {
      return null;
    }

    return (
      <Block ref={forwardingRef} alignment={alignment} style={style}>
        <Coupon
          {...forwardingProps}
          font={currentFont}
          width={isFullWidth ? '100%' : undefined}
          onCopied={handleCopy}
        >
          {code}
        </Coupon>
      </Block>
    );
  }
);
